import React, { useEffect, useMemo, useState } from 'react';
import { List, useTable, CreateButton } from '@refinedev/antd';
import { useList } from '@refinedev/core';
import { Table, Select, Space, Tag, Button, Card, Skeleton, Row, Col, Statistic, Segmented, Drawer, Input, Form } from 'antd';
import {
  EyeOutlined,
  EditOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  UnorderedListOutlined,
  HourglassOutlined,
  CarOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  StopOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { usePermission } from '../../hooks/usePermission';
import { t } from '../../i18n';
import { apiClient } from '../../lib/api-client';

interface Trip {
  id: number;
  tripType: 'INTERCITY_TAXI' | 'INTERREGION_TAXI' | 'POSTAGE' | 'CARGO' | 'YOL_YOLAKAY' | 'SHAHARLAR_ARO';
  status: string;
  pickupAddress: string;
  destinationAddress: string;
  finalPrice: number | null;
  priceEstimate: number;
  // Tariff + passengerCount are present on InterregionTaxiTrip rows;
  // null/undefined for the other trip types where the seat-label badge
  // doesn't apply.
  tariff?: 'ECONOM' | 'STANDARD' | 'COMFORT' | 'BUSINESS' | 'SEVEN_PLUS' | null;
  passengerCount?: number | null;
  createdAt: string;
  requestedAt: string;
  acceptedAt?: string | null;
  canceledAt?: string | null;
  cancelledAt?: string | null;
  client: {
    fullName: string;
    phone: string;
  };
  driver: {
    fullName: string;
    phone: string;
  } | null;
  pickupRegion: { nameUz: string } | null;
  destinationRegion: { nameUz: string } | null;
  pickupDistrict: { nameUz: string } | null;
  destinationDistrict: { nameUz: string } | null;
}

/// Per-tariff seat cap on Viloyatlar Aro trips. Mirrors mobile
/// ViloyatlarAroTariffExt.maxSeats: Ekonom 4, Standard / Komfort / Biznes 3,
/// Minivan no full-salon concept (returns null).
const seatCapForTariff = (tariff?: string | null): number | null => {
  switch (tariff) {
    case 'ECONOM':
      return 4;
    case 'STANDARD':
    case 'COMFORT':
    case 'BUSINESS':
      return 3;
    default:
      return null;
  }
};

/// Returns "{N} ta joy" / "To'liq salon" for INTERREGION_TAXI rows; null
/// for other trip types and for Minivan (which has no full-salon
/// concept). Stays in Uzbek to match the existing badge convention used
/// across the rest of the admin trip area.
const viloyatlarAroSeatLabel = (trip: Trip): string | null => {
  if (trip.tripType !== 'INTERREGION_TAXI') return null;
  const seats = trip.passengerCount ?? 0;
  if (seats <= 0) return null;
  const cap = seatCapForTariff(trip.tariff);
  if (cap != null && seats >= cap) return "To'liq salon";
  return `${seats} ta joy`;
};

const WAITING_STATUSES = ['REQUESTED', 'ACCEPTED', 'DRIVER_ARRIVED'];

const formatWaitingTime = (startAt: string, endAt?: string | null): string => {
  const end = endAt ? new Date(endAt).getTime() : Date.now();
  const diff = end - new Date(startAt).getTime();
  if (diff < 0) return '0s';
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}${t('time.day')} ${hours}${t('time.hour')} ${minutes}${t('time.minute')}`;
  if (hours > 0) return `${hours}${t('time.hour')} ${minutes}${t('time.minute')}`;
  if (minutes > 0) return `${minutes}${t('time.minute')} ${seconds}${t('time.second')}`;
  return `${seconds}${t('time.second')}`;
};

// Color thresholds for the waiting-time pill. Tuned to the typical SafarGo
// driver-assignment SLA: under 3 min is "fast", over 10 min flags as "slow".
const waitingColorFor = (startAt: string, endAt?: string | null): string => {
  const end = endAt ? new Date(endAt).getTime() : Date.now();
  const minutes = (end - new Date(startAt).getTime()) / 60000;
  if (minutes < 3) return 'success';
  if (minutes < 10) return 'warning';
  return 'error';
};

const WaitingTimer = React.memo(({ requestedAt }: { requestedAt: string }) => {
  const [, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Tag icon={<ClockCircleOutlined />} color={waitingColorFor(requestedAt)}>
      {formatWaitingTime(requestedAt)}
    </Tag>
  );
});

WaitingTimer.displayName = 'WaitingTimer';

type TripDateRange = 'today' | 'yesterday' | 'thisWeek' | 'thisMonth' | 'thisYear' | 'allTime';

// Resolve a Segmented value to ISO `fromDate` / `toDate` for the backend.
// `allTime` returns nothing so the query stops constraining by date.
const resolveRange = (range: TripDateRange): { fromDate?: string; toDate?: string } => {
  const now = new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  switch (range) {
    case 'yesterday': {
      const start = new Date(today);
      start.setDate(start.getDate() - 1);
      const end = new Date(today);
      end.setMilliseconds(-1);
      return { fromDate: start.toISOString(), toDate: end.toISOString() };
    }
    case 'thisWeek': {
      // ISO week starting Monday.
      const start = new Date(today);
      const day = (start.getDay() + 6) % 7;
      start.setDate(start.getDate() - day);
      return { fromDate: start.toISOString(), toDate: now.toISOString() };
    }
    case 'thisMonth': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { fromDate: start.toISOString(), toDate: now.toISOString() };
    }
    case 'thisYear': {
      const start = new Date(today.getFullYear(), 0, 1);
      return { fromDate: start.toISOString(), toDate: now.toISOString() };
    }
    case 'allTime':
      return {};
    case 'today':
    default:
      return { fromDate: today.toISOString(), toDate: now.toISOString() };
  }
};

export const TripList = () => {
  const navigate = useNavigate();
  const { can } = usePermission();
  const [tripTypeFilter, setTripTypeFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [range, setRange] = useState<TripDateRange>('today');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Tracked separately from `searchTerm` so typing in the drawer doesn't
  // refetch on every keystroke — the active value only updates on submit.
  const [activeSearch, setActiveSearch] = useState<string>('');
  const [pickupRegionId, setPickupRegionId] = useState<number | undefined>();
  const [destinationRegionId, setDestinationRegionId] = useState<
    number | undefined
  >();
  const [regions, setRegions] = useState<{ id: number; nameUz: string }[]>([]);

  useEffect(() => {
    apiClient
      .get<{ id: number; nameUz: string }[]>('/admin/regions')
      .then((res) => {
        const arr = Array.isArray(res.data)
          ? res.data
          : (res.data as { data?: { id: number; nameUz: string }[] })?.data ||
            [];
        setRegions(arr);
      })
      .catch(() => setRegions([]));
  }, []);

  // Re-derive only when `range` changes so we don't churn refine's filter
  // diffing on every render.
  const dateFilters = useMemo(() => {
    const { fromDate, toDate } = resolveRange(range);
    const out: Array<{ field: string; operator: 'gte' | 'lte'; value: string }> = [];
    if (fromDate) out.push({ field: 'fromDate', operator: 'gte', value: fromDate });
    if (toDate) out.push({ field: 'toDate', operator: 'lte', value: toDate });
    return out;
  }, [range]);

  const { tableProps } = useTable<Trip>({
    resource: 'admin/trips',
    syncWithLocation: true,
    pagination: {
      pageSize: 20,
    },
    filters: {
      permanent: [
        ...(tripTypeFilter ? [{ field: 'tripType', operator: 'eq' as const, value: tripTypeFilter }] : []),
        ...(statusFilter ? [{ field: 'status', operator: 'eq' as const, value: statusFilter }] : []),
        ...(activeSearch ? [{ field: 'search', operator: 'contains' as const, value: activeSearch }] : []),
        ...(pickupRegionId ? [{ field: 'pickupRegionId', operator: 'eq' as const, value: pickupRegionId }] : []),
        ...(destinationRegionId ? [{ field: 'destinationRegionId', operator: 'eq' as const, value: destinationRegionId }] : []),
        ...dateFilters,
      ],
    },
    sorters: {
      initial: [{ field: 'requestedAt', order: 'desc' }],
    },
  });

  const countQuery = (status?: string) => ({
    resource: 'admin/trips',
    pagination: { current: 1, pageSize: 1 },
    filters: [
      ...(status ? [{ field: 'status', operator: 'eq' as const, value: status }] : []),
      ...(activeSearch ? [{ field: 'search', operator: 'contains' as const, value: activeSearch }] : []),
      ...(pickupRegionId ? [{ field: 'pickupRegionId', operator: 'eq' as const, value: pickupRegionId }] : []),
      ...(destinationRegionId ? [{ field: 'destinationRegionId', operator: 'eq' as const, value: destinationRegionId }] : []),
      ...dateFilters,
    ],
  });

  const { data: totalData, isLoading: isTotalLoading } = useList<Trip>(countQuery());
  const { data: requestedData, isLoading: isRequestedLoading } = useList<Trip>(countQuery('REQUESTED'));
  const { data: acceptedData, isLoading: isAcceptedLoading } = useList<Trip>(countQuery('ACCEPTED'));
  const { data: inProgressData, isLoading: isInProgressLoading } = useList<Trip>(countQuery('IN_PROGRESS'));
  const { data: completedData, isLoading: isCompletedLoading } = useList<Trip>(countQuery('COMPLETED'));
  const { data: cancelledData, isLoading: isCancelledLoading } = useList<Trip>(countQuery('CANCELLED'));

  const totalCount = totalData?.total ?? 0;
  const requestedCount = requestedData?.total ?? 0;
  const acceptedCount = acceptedData?.total ?? 0;
  const inProgressCount = inProgressData?.total ?? 0;
  const completedCount = completedData?.total ?? 0;
  const cancelledCount = cancelledData?.total ?? 0;

  const tripTypeLabels: Record<string, string> = {
    INTERCITY_TAXI: t('tripType.intercityTaxi'),
    INTERREGION_TAXI: t('tripType.interregionTaxi'),
    SHAHARLAR_ARO: t('tripType.shaharlarAro'),
    POSTAGE: t('tripType.postage'),
    CARGO: t('tripType.cargo'),
    YOL_YOLAKAY: t('tripType.yolYolakay'),
  };

  const tripTypeColors: Record<string, string> = {
    INTERCITY_TAXI: 'blue',
    INTERREGION_TAXI: 'purple',
    SHAHARLAR_ARO: 'magenta',
    POSTAGE: 'orange',
    CARGO: 'cyan',
    YOL_YOLAKAY: 'green',
  };

  const statusLabels: Record<string, string> = {
    REQUESTED: t('status.requested'),
    ACCEPTED: t('status.accepted'),
    DRIVER_ARRIVED: t('status.driverArrived'),
    PICKED_UP: t('status.pickedUp'),
    ON_THE_WAY: t('status.onTheWay'),
    IN_PROGRESS: t('status.inProgress'),
    COMPLETED: t('status.completed'),
    CANCELLED: t('status.cancelled'),
    INTERRUPTED: t('status.interrupted'),
    NO_SHOW: t('status.noShow'),
    // Yol-Yolakay statuses
    ACTIVE: t('status.active'),
    MATCHED: t('status.matched'),
    EXPIRED: t('status.expired'),
  };

  const statusColors: Record<string, string> = {
    REQUESTED: 'orange',
    ACCEPTED: 'blue',
    DRIVER_ARRIVED: 'cyan',
    PICKED_UP: 'geekblue',
    ON_THE_WAY: 'processing',
    IN_PROGRESS: 'processing',
    COMPLETED: 'green',
    CANCELLED: 'red',
    INTERRUPTED: 'volcano',
    NO_SHOW: 'magenta',
    // Yol-Yolakay statuses
    ACTIVE: 'green',
    MATCHED: 'blue',
    EXPIRED: 'default',
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + ' ' + t('common.sum');
  };

  const columns = [
    {
      title: t('common.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: t('table.tripType'),
      dataIndex: 'tripType',
      key: 'tripType',
      render: (type: string, record: Trip) => {
        const seatLabel = viloyatlarAroSeatLabel(record);
        return (
          <Space size={4} wrap>
            <Tag color={tripTypeColors[type]}>{tripTypeLabels[type] || type}</Tag>
            {seatLabel && (
              <Tag color="cyan" style={{ marginRight: 0 }}>
                {seatLabel}
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: t('table.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status]}>{statusLabels[status] || status}</Tag>
      ),
    },
    {
      title: t('table.waitingTime'),
      key: 'waitingTime',
      render: (_: unknown, record: Trip) => {
        const requestedAt = record.requestedAt || record.createdAt;
        if (WAITING_STATUSES.includes(record.status)) {
          return <WaitingTimer requestedAt={requestedAt} />;
        }
        // For terminal/intermediate states, freeze the timer at the moment the
        // wait actually ended: driver accepted, or trip cancelled.
        const endAt =
          record.acceptedAt ||
          record.canceledAt ||
          record.cancelledAt ||
          null;
        return (
          <Tag icon={<ClockCircleOutlined />} color={waitingColorFor(requestedAt, endAt)}>
            {formatWaitingTime(requestedAt, endAt)}
          </Tag>
        );
      },
    },
    {
      title: t('table.route'),
      key: 'route',
      width: 200,
      render: (_: unknown, record: any) => {
        const pickup = [
          record.pickupRegion?.nameUz,
          record.pickupDistrict?.nameUz,
        ].filter(Boolean).join(', ');
        const dest = [
          record.destinationRegion?.nameUz,
          record.destinationDistrict?.nameUz,
        ].filter(Boolean).join(', ');
        const pickupText = pickup || record.pickupAddress || '-';
        const destText = dest || record.destinationAddress || '-';
        const destCount = record.destinations?.length;
        return (
          <div style={{ fontSize: 12, maxWidth: 180 }}>
            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={pickupText}>
              <EnvironmentOutlined style={{ color: '#52c41a' }} /> {pickupText}
            </div>
            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={destText}>
              <EnvironmentOutlined style={{ color: '#f5222d' }} /> {destText}
            </div>
            {destCount > 1 && (
              <div style={{ color: '#7F8C8D', marginTop: 2 }}>+{destCount - 1} {t('trips.moreDestinations')}</div>
            )}
          </div>
        );
      },
    },
    {
      title: t('table.client'),
      dataIndex: 'client',
      key: 'client',
      render: (client: Trip['client']) => (
        <div>
          <div>{client?.fullName || t('common.noName')}</div>
          <div style={{ color: '#7F8C8D', fontSize: 12 }}>{client?.phone}</div>
        </div>
      ),
    },
    {
      title: t('table.driver'),
      dataIndex: 'driver',
      key: 'driver',
      render: (driver: Trip['driver']) =>
        driver ? (
          <div>
            <div>{driver.fullName || t('common.noName')}</div>
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{driver.phone}</div>
          </div>
        ) : (
          <span style={{ color: '#7F8C8D' }}>-</span>
        ),
    },
    {
      title: t('table.price'),
      dataIndex: 'finalPrice',
      key: 'finalPrice',
      render: (price: number | null, record: Trip) =>
        formatCurrency(price != null && price > 0 ? price : record.priceEstimate),
    },
    {
      title: t('table.date'),
      key: 'requestedAt',
      render: (_: unknown, record: Trip) => {
        const date = record.requestedAt || record.createdAt;
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, '0');
        const mins = String(d.getMinutes()).padStart(2, '0');
        return (
          <div>
            <div>{day}.{month}.{year}</div>
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{hours}:{mins}</div>
          </div>
        );
      },
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_: unknown, record: Trip) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/trips/${record.tripType}/${record.id}`)}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/trips/${record.tripType}/${record.id}/edit`)}
          />
        </Space>
      ),
    },
  ];

  return (
    <List
      title={t('trips.title')}
      headerButtons={
        <Space wrap>
          <Segmented<TripDateRange>
            value={range}
            onChange={(val) => setRange(val as TripDateRange)}
            options={[
              { label: t('dashboard.range.today'), value: 'today' },
              { label: t('dashboard.range.yesterday'), value: 'yesterday' },
              { label: t('dashboard.range.thisWeek'), value: 'thisWeek' },
              { label: t('dashboard.range.thisMonth'), value: 'thisMonth' },
              { label: t('dashboard.range.thisYear'), value: 'thisYear' },
              { label: t('dashboard.range.allTime'), value: 'allTime' },
            ]}
          />
          <Select
            placeholder={t('common.type')}
            allowClear
            style={{ width: 140 }}
            value={tripTypeFilter}
            onChange={(value) => setTripTypeFilter(value)}
            options={Object.entries(tripTypeLabels).map(([value, label]) => ({
              value,
              label,
            }))}
          />
          <Select
            placeholder={t('common.status')}
            allowClear
            style={{ width: 140 }}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value)}
            options={Object.entries(statusLabels).map(([value, label]) => ({
              value,
              label,
            }))}
          />
          <Select
            showSearch
            allowClear
            placeholder="Qayerdan (viloyat)"
            style={{ width: 180 }}
            value={pickupRegionId}
            onChange={(v) => setPickupRegionId(v)}
            optionFilterProp="label"
            options={regions.map((r) => ({ value: r.id, label: r.nameUz }))}
          />
          <Select
            showSearch
            allowClear
            placeholder="Qayerga (viloyat)"
            style={{ width: 180 }}
            value={destinationRegionId}
            onChange={(v) => setDestinationRegionId(v)}
            optionFilterProp="label"
            options={regions.map((r) => ({ value: r.id, label: r.nameUz }))}
          />
          <Button
            icon={<FilterOutlined />}
            onClick={() => {
              setSearchTerm(activeSearch);
              setFiltersOpen(true);
            }}
          >
            Filtrlar
            {activeSearch ? (
              <Tag color="blue" style={{ marginLeft: 6, marginRight: 0 }}>
                1
              </Tag>
            ) : null}
          </Button>
          {can('trips', 'create') && <CreateButton>{t('trips.newTrip')}</CreateButton>}
        </Space>
      }
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card>
            <Statistic
              title="Jami"
              value={totalCount}
              loading={isTotalLoading}
              prefix={<UnorderedListOutlined style={{ color: '#595959' }} />}
              valueStyle={{ color: '#262626' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card>
            <Statistic
              title={statusLabels.REQUESTED}
              value={requestedCount}
              loading={isRequestedLoading}
              prefix={<HourglassOutlined style={{ color: '#fa8c16' }} />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card>
            <Statistic
              title={statusLabels.ACCEPTED}
              value={acceptedCount}
              loading={isAcceptedLoading}
              prefix={<CarOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card>
            <Statistic
              title={statusLabels.IN_PROGRESS}
              value={inProgressCount}
              loading={isInProgressLoading}
              prefix={<SyncOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card>
            <Statistic
              title={statusLabels.COMPLETED}
              value={completedCount}
              loading={isCompletedLoading}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card>
            <Statistic
              title={statusLabels.CANCELLED}
              value={cancelledCount}
              loading={isCancelledLoading}
              prefix={<StopOutlined style={{ color: '#f5222d' }} />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      <Table
        {...tableProps}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        columns={columns}
        rowKey="id"
        scroll={{ x: 1200 }}
        loading={
          tableProps.loading
            ? { indicator: <Card bordered={false}><Skeleton active paragraph={{ rows: 8 }} /></Card> }
            : false
        }
      />

      <Drawer
        title="Qo'shimcha filtrlar"
        placement="right"
        width={360}
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        footer={
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button
              onClick={() => {
                setSearchTerm('');
                setActiveSearch('');
                setFiltersOpen(false);
              }}
            >
              Tozalash
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setActiveSearch(searchTerm.trim());
                setFiltersOpen(false);
              }}
            >
              Qo'llash
            </Button>
          </Space>
        }
      >
        <Form layout="vertical">
          <Form.Item
            label="Qidiruv"
            help="Mijoz yoki haydovchining ismi yoki telefoni bo'yicha qidiring."
          >
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Masalan: 99890… yoki Aliyev"
              allowClear
              onPressEnter={() => {
                setActiveSearch(searchTerm.trim());
                setFiltersOpen(false);
              }}
            />
          </Form.Item>
        </Form>
      </Drawer>
    </List>
  );
};
